import React, { useEffect } from "react";
import { useTranslate, useOne, useUpdate, useApiUrl, useCustomMutation } from "@refinedev/core";
import { Form, Input, Modal, Typography, Space, Button, Card, Row, Col, Spin, message } from "antd";
import { ApiOutlined, SaveOutlined } from "@ant-design/icons";

const { Title } = Typography;

type ServiceProviderEditModalProps = {
  open: boolean;
  serviceProviderId: number | string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const ServiceProviderEditModal: React.FC<ServiceProviderEditModalProps> = ({
  open,
  serviceProviderId,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm();

  const { data, isLoading } = useOne({
    resource: "service-providers",
    id: serviceProviderId ?? "",
    queryOptions: {
      enabled: open && !!serviceProviderId,
    },
  });

  const record = data?.data;

  const { mutate: update, isLoading: isUpdating } = useUpdate();
  const { mutate: testAuthentication, isLoading: isTestingAuth } = useCustomMutation();

  useEffect(() => {
    if (record && open) {
      form.setFieldsValue({
        name: record.name,
        apiEndpoint: record.apiEndpoint,
        clientId: record.clientId,
        clientSecret: record.clientSecret,
        pushAccountId: record.pushAccountId ?? "",
        pushSchemaId: record.pushSchemaId ?? "",
      });
    }
    if (!open) {
      form.resetFields();
    }
  }, [record, open, form]);

  const handleFinish = (values: any) => {
    if (!serviceProviderId) return;

    update(
      {
        resource: "service-providers",
        id: serviceProviderId,
        values,
      },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  const handleTestAuthentication = async () => {
    if (!serviceProviderId) return;

    try {
      const values = form.getFieldsValue([
        "apiEndpoint",
        "clientId",
        "clientSecret",
      ]);
      const apiEndpoint = String(values.apiEndpoint ?? "").trim();
      const clientId = String(values.clientId ?? "").trim();
      const clientSecret = String(values.clientSecret ?? "").trim();

      if (apiEndpoint) {
        await form.validateFields(["apiEndpoint"]);
      }

      const payload: Record<string, string> = {};
      if (apiEndpoint) payload.apiEndpoint = apiEndpoint;
      if (clientId) payload.clientId = clientId;
      if (clientSecret) payload.clientSecret = clientSecret;

      testAuthentication(
        {
          url: `${apiUrl}/service-providers/${serviceProviderId}/test-authentication`,
          method: "post",
          values: payload,
        },
        {
          successNotification: false,
          errorNotification: false,
          onSuccess: () => {
            message.success(_t("service-providers.notifications.testAuthSuccess"));
          },
          onError: () => {
            message.error(_t("service-providers.notifications.testAuthError"));
          },
        },
      );
    } catch {
      // validation errors are shown by the form
    }
  };

  return (
    <Modal
      title={_t("service-providers.titles.edit")}
      open={open}
      onCancel={onCancel}
      width={700}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{_t("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isUpdating}
            onClick={() => form.submit()}
          >
            {_t("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="!mt-4"
        >
          <Form.Item
            label={_t("service-providers.fields.name")}
            name="name"
            rules={[{ required: true, message: _t("fields.errors.required") }]}
          >
            <Input placeholder={_t("service-providers.fields.name")} />
          </Form.Item>

          <Card size="small">
            <Title level={5}>{_t("service-providers.fields.apiAndCredentials")}</Title>
            <Form.Item
              label={_t("service-providers.fields.apiEndpoint")}
              name="apiEndpoint"
              rules={[
                { type: "url", message: _t("service-providers.errors.invalidUrl") },
              ]}
            >
              <Input placeholder="https://api.example.com" />
            </Form.Item>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={_t("service-providers.fields.clientId")}
                  name="clientId"
                >
                  <Input placeholder={_t("service-providers.fields.clientId")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={_t("service-providers.fields.clientSecret")}
                  name="clientSecret"
                  extra={_t("service-providers.fields.clientSecretEditHint")}
                >
                  <Input.Password
                    placeholder={_t("service-providers.fields.clientSecret")}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Button
              htmlType="button"
              icon={<ApiOutlined />}
              loading={isTestingAuth}
              onClick={handleTestAuthentication}
              className="mb-4"
            >
              {_t("service-providers.actions.testAuthentication")}
            </Button>
            <Form.Item
              label={_t("service-providers.fields.pushAccountId")}
              name="pushAccountId"
              rules={[
                { required: true, message: _t("fields.errors.required") },
                {
                  whitespace: true,
                  message: _t("fields.errors.required"),
                },
              ]}
            >
              <Input placeholder={_t("service-providers.fields.pushAccountId")} />
            </Form.Item>
            <Form.Item
              label={_t("service-providers.fields.pushSchemaId")}
              name="pushSchemaId"
              rules={[
                { required: true, message: _t("fields.errors.required") },
                {
                  whitespace: true,
                  message: _t("fields.errors.required"),
                },
              ]}
            >
              <Input placeholder={_t("service-providers.fields.pushSchemaId")} />
            </Form.Item>
          </Card>
        </Form>
      </Spin>
    </Modal>
  );
}

